import * as React from "react";
import {none, option, Option, some} from "ts-option";
import {api} from "../../api/api";
import PageHeaderView from "../body/PageHeaderView";
import PageContentView from "../body/PageContentView";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import "./operationErrorsList.scss"
import ReactTable, {CellInfo, Column, RowInfo} from "react-table";
import DateUtils from "../../utils/DateUtils";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import PageSelectionComponent from "../v5/table/paginator/PageSelectionComponent";
import WorkspaceContainer from "../v5/table/workspace/WorkspaceContainer";
import {OperationErrorApiDto, RetriggerData} from "../../api/model/rest";
import TextUtils from "../../utils/TextUtils";
import Toast from "../helper/Toast";
import moment = require("moment");
import OperationErrorDetailsModal from "../codatOperationError/errorDetails/OperationErrorDetailsModal";
import RetriggeredDataInfo from "../codatOperationError/retrigeredData/RetriggeredDataInfo";

interface State {
	items: Option<OperationErrorApiDto[]>
	page: number,
	totalSize: number,
	errorDetails: Option<OperationErrorApiDto>,
	idOfOperationInProgress: Option<string>
}

export default class XeroOperationErrorsList extends React.PureComponent<{}, State> {

	columns: Column[] = [
		{
			Header: "Creation date time",
			accessor: "dateTime",
			Cell: (props: CellInfo) => moment(props.value).format(DateUtils.JOB_COMPLETE_DATE_FORMAT),
			maxWidth: 135
		},
		{Header: "Partner Brand", accessor: "partnerBrand.name", maxWidth: 200},
		{
			Header: "Reference number",
			accessor: "data.referenceNumber",
			Cell: (props: CellInfo) => props.value,
			maxWidth: 200
		},
		{
			Header: "Request type",
			accessor: "type",
			Cell: (props: CellInfo) => props.value.replaceUnderscoresWithSpaces(),
			maxWidth: 170
		},
		{
			Header: "Error message",
			accessor: "data",
			Cell: (props) => TextUtils.textEllipsis(JSON.stringify(props.value), 200),
			maxWidth: 850
		},
		{
			Header: "Actions", accessor: "retriggerData", Cell: props => {
				const isItemActionLoading = this.state.idOfOperationInProgress.map(id => props.original.id == id).getOrElse(false)
				const value: RetriggerData = props.value
				return value.retriggered ? <RetriggeredDataInfo data={value}/> :
					<ButtonPrimary label={"Retrigger"}
								   onClick={() => this.retrigger(props.original.id)}
								   isLoading={isItemActionLoading}
								   disabled={this.state.idOfOperationInProgress.isDefined}
					/>
			}, maxWidth: 200
		},
	]

	constructor() {
		super({});
		this.state = {totalSize: 0, page: 1, items: none, errorDetails: none, idOfOperationInProgress: none}
	}

	componentDidMount() {
		this.fetchErrors();
	}

	render() {
		return (
			<div className={"c-operation-errors"}>
				<PageHeaderView header={"Xero Operation Errors"}/>
				<PageContentView>
					<div className={"c-operation-errors_pagination-wrapper"}>
						<PageSelectionComponent itemsNumber={this.state.totalSize}
												itemsPerPage={WorkspaceContainer.PAGE_SIZE}
												currentPageIndex={this.state.page}
												onChange={this.fetchErrors}/>
					</div>
					<ReactTable data={this.state.items.getOrElse([])}
								loading={this.state.items.isEmpty}
								columns={this.columns}
								sortable={false}
								showPaginationBottom={false}
								minRows={0}
								resizable={false}
								getTrProps={this.getTrProps}/>
				</PageContentView>
				{this.state.errorDetails.map(ed => <OperationErrorDetailsModal onClose={() => this.setState({errorDetails: none})}
																			   errorDetails={ed}/>).orNull}
			</div>
		)
	}

	getTrProps = (state: any, rowInfo: RowInfo) => {
		const props: { [key: string]: any } = {onClick: () => this.setState({errorDetails: some(rowInfo.original)})}
		if (rowInfo.original.retriggerData.retriggered) {
			props["className"] = "c-item-disabled"
		}
		return props
	}

	fetchErrors = (page: number = this.state.page) => {
		this.setState({items: none}, () =>
			api.instance.operationErrorsApi.fetchXeroOperationErrors(page)
				.then(
					data => {
						const maxPageNumber = Math.floor(data.totalItems / WorkspaceContainer.PAGE_SIZE) + 1
						if (page > maxPageNumber) {
							this.fetchErrors(1);
						}
						else {
							this.setState({items: option(data.items), totalSize: data.totalItems, page})
						}
					})
				.catch(e => {
					this.setState({totalSize: 0, items: option([])})
					notifyBugsnagAndShowToast(e, "Fetch operation errors")
				})
		)
	}

	retrigger = (operationErrorId: string) => {
		this.setState({idOfOperationInProgress: some(operationErrorId)}, () =>
			api.instance.operationErrorsApi.retriggerXeroOperation(operationErrorId)
				.then((resp) => {
					this.setState({idOfOperationInProgress: none})
					if (resp.result == "SUCCESS") {
						Toast.success("Operation retriggered successfully", "Retrigger operation");
						this.fetchErrors()
					}
					else {
						Toast.error("An error has occurred again. See the new error details in the list", "Retrigger operation");
						this.fetchErrors(1);
					}
				})
				.catch(e => {
					this.setState({idOfOperationInProgress: none})
					notifyBugsnagAndShowToast(e, "Retrigger operation")
					this.fetchErrors();
				}))
	}
}